import { createContext } from "react";
import openSocket from "socket.io-client";
import { jwtDecode } from "jwt-decode";
import { clearSessionKeys } from "../../helpers/sessionStorageKeys";

/**
 * NOTA SOBRE ERROS DE WEBSOCKET NO CONSOLE DO NAVEGADOR:
 * 
 * Você pode ver erros como "WebSocket connection to '...' failed" no console.
 * Esses erros são do NAVEGADOR (websocket.js interno do socket.io-client) e 
 * NÃO PODEM ser completamente suprimidos via JavaScript.
 * 
 * ISSO É NORMAL E ESPERADO quando:
 * - O servidor está reiniciando
 * - Há problemas temporários de rede
 * - O navegador tenta WebSocket antes de fazer fallback para polling
 * 
 * O sistema está configurado para:
 * 1. Em PRODUÇÃO: Usar polling primeiro (evita maioria dos erros)
 * 2. Fazer upgrade para WebSocket quando estável
 * 3. Reconectar automaticamente
 * 4. Exibir log de SUCESSO quando reconectar
 */

// Incidente 21/09/2026 (alertas de pool a cada minuto, 130 tickets + 117
// contatos por rajada): um único usuário cuja aba em segundo plano reconectava
// a cada 60s re-entrava em TODAS as salas dos tickets que tinha aberto no dia.
// Três defeitos deste wrapper se combinavam:
//  1. `SocketManager.socketReady` só virava true por um `once("ready")` na
//     criação do socket; depois da primeira queda ficava false para sempre, e
//     todo `on("ready", cb)` passava a ser adiado para a PRÓXIMA reconexão —
//     inclusive o join da sala do ticket recém-aberto (que só entrava depois
//     de outra queda, ou nunca).
//  2. `disconnect()` limpava `joins`/`callbacks` mas não removia do socket real
//     nem o `once("ready")` pendente nem o `connectHandler`. O `once` disparava
//     depois do cleanup, empurrava o join num wrapper já descartado, e o
//     `connectHandler` desse zumbi re-emitia o join em toda reconexão seguinte.
//  3. `rawSocket.CONNECTED` não existe no socket.io-client (é `connected`), então
//     os `leave*` nunca eram emitidos.
class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = socketManager.currentSocket;
    this.callbacks = [];
    this.joins = [];
    this.readyListeners = [];
    this.connectHandler = null;
    this.pendingRefresh = null;
    this.disposed = false;

    // Configurar handler de reconexão
    this.setupReconnectHandler();
  }

  setupReconnectHandler() {
    // Remover handler anterior se existir
    if (this.connectHandler) {
      this.rawSocket.off("connect", this.connectHandler);
    }

    this.connectHandler = () => {
      if (this.disposed) return;
      if (!this.rawSocket.recovered) {
        if (this.pendingRefresh) {
          this.rawSocket.off("ready", this.pendingRefresh);
        }
        // Só re-entra nas salas que JÁ existiam antes desta conexão: joins
        // feitos entre o connect e o ready (ex.: `on("ready")` adiado de um
        // componente montado durante a queda) se emitem sozinhos.
        const snapshot = this.joins.slice();
        const refreshJoinsOnReady = () => {
          this.pendingRefresh = null;
          if (this.disposed) return;
          for (const j of snapshot) {
            if (!this.joins.includes(j)) continue; // saiu da sala nesse meio-tempo
            this.rawSocket.emit(`join${j.event}`, ...j.params);
          }
        };
        this.pendingRefresh = refreshJoinsOnReady;

        for (const j of this.callbacks) {
          this.rawSocket.off(j.event, j.callback);
          this.rawSocket.on(j.event, j.callback);
        }

        this.rawSocket.once("ready", refreshJoinsOnReady);
      }
    };

    this.rawSocket.on("connect", this.connectHandler);
  }

  on(event, callback) {
    if (event === "ready" || event === "connect") {
      // Dispara agora se o servidor já deu "ready" nesta conexão; senão fica
      // pendente UMA vez e é removido no disconnect() se o componente sair antes.
      const listener = this.socketManager.onReady(callback);
      if (listener) {
        this.readyListeners.push(listener);
      }
      return;
    }
    this.callbacks.push({event, callback});
    return this.rawSocket.on(event, callback);
  }
  
  off(event, callback) {
    const i = this.callbacks.findIndex((c) => c.event === event && c.callback === callback);
    if (i !== -1) {
      this.callbacks.splice(i, 1);
    }
    return this.rawSocket.off(event, callback);
  }
  
  emit(event, ...params) {
    if (event.startsWith("join")) {
      const room = event.substring(4);
      const key = JSON.stringify(params);
      // Mesmo join repetido pelo mesmo componente (ex.: "ready" + reconexão) não
      // vira duas entradas — o backend conta joins e só sai da sala quando o
      // contador zera, então join e leave precisam ficar 1:1.
      if (this.joins.some((j) => j.event === room && JSON.stringify(j.params) === key)) {
        return;
      }
      this.joins.push({ event: room, params });
    } else if (event.startsWith("leave")) {
      const room = event.substring(5);
      const key = JSON.stringify(params);
      const i = this.joins.findIndex((j) => j.event === room && JSON.stringify(j.params) === key);
      if (i !== -1) {
        this.joins.splice(i, 1);
      }
    }
    return this.rawSocket.emit(event, ...params);
  }
  
  disconnect() {
    this.disposed = true;

    // Só emite leave se o socket ainda está conectado
    if (this.rawSocket && this.rawSocket.connected) {
      for (const j of this.joins) {
        this.rawSocket.emit(`leave${j.event}`, ...j.params);
      }
    }
    this.joins = [];
    
    // Remove all callbacks
    for (const c of this.callbacks) {
      if (this.rawSocket) {
        this.rawSocket.off(c.event, c.callback);
      }
    }
    this.callbacks = [];

    if (this.rawSocket) {
      for (const l of this.readyListeners) {
        this.rawSocket.off("ready", l);
      }
      if (this.pendingRefresh) {
        this.rawSocket.off("ready", this.pendingRefresh);
      }
      if (this.connectHandler) {
        this.rawSocket.off("connect", this.connectHandler);
      }
    }
    this.readyListeners = [];
    this.pendingRefresh = null;
    this.connectHandler = null;
  }

  // Estado real da conexão (socket.io-client expõe `connected`, não `CONNECTED`)
  get CONNECTED() {
    return this.rawSocket?.connected || false;
  }
  
  // Expor método connect para reconexão manual
  connect() {
    if (this.rawSocket && typeof this.rawSocket.connect === 'function') {
      return this.rawSocket.connect();
    }
  }
}

class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,
  lastDisconnectTime: null,
  consecutiveErrors: 0,
  gracePeriodMs: 30000, // 30 segundos de tolerância
  fastReconnectionMs: 5000, // reconexões < 5s são silenciosas
  reconnectionTimeout: null,
  reconnectWindowMs: 60000,
  maxReconnectsPerWindow: 30,
  reconnectWindowStart: 0,
  reconnectAttemptsInWindow: 0,
  reconnectCooldownMs: 45000,
  reconnectCooldownUntil: 0,
  pollingPausedUntil: 0,

  // Ponte para a camada de autenticação (useAuth registra a função aqui). O
  // SocketManager é um singleton de módulo, sem acesso ao estado do React, então
  // não pode renovar o token sozinho — só avisar quem sabe fazê-lo.
  onTokenStale: null,
  lastTokenStaleAt: 0,
  tokenStaleDebounceMs: 15000,

  // Um token vencido não é motivo para apagar a sessão: quem decide isso é a
  // camada de auth. Antes o getSocket removia token/companyId/userId do
  // localStorage, o que derrubava as assinaturas de TODOS os componentes (que
  // leem companyId de lá) e ainda silenciava os 401 — o usuário continuava
  // "logado" e sem conversas, contatos ou atendimentos.
  requestTokenRenewal: function() {
    const now = Date.now();
    if (now - this.lastTokenStaleAt < this.tokenStaleDebounceMs) {
      return;
    }
    this.lastTokenStaleAt = now;
    if (typeof this.onTokenStale === "function") {
      this.onTokenStale();
    }
  },

  // Token usado no handshake do socket vivo. O socket.io guarda a query na
  // CRIAÇÃO e reenvia o mesmo io.opts em toda reconexão — sem reescrever isto,
  // um token renovado nunca chega ao servidor e o handshake é recusado para
  // sempre, a cada 2 segundos.
  currentSocketToken: null,

  reconnectWithTokenTimer: null,

  // Passa a usar o token novo. Duas situações bem diferentes:
  //
  //  - socket CONECTADO: o servidor já validou este socket e não revalida uma
  //    conexão estabelecida. Basta atualizar a query para que a PRÓXIMA
  //    reconexão use a credencial nova — derrubar a conexão aqui só geraria
  //    churn (o X-New-Token pode chegar em várias respostas simultâneas, cada
  //    uma com um token diferente).
  //  - socket CAÍDO: aí sim reconecta, com debounce para agrupar rajadas e não
  //    esbarrar no throttle anti-loop do servidor (1s).
  //
  // Sem atualizar io.opts.query o socket.io reenviaria eternamente o token do
  // handshake original, e o servidor recusaria a cada tentativa.
  applyToken: function(newToken) {
    if (!newToken || !this.currentSocket) {
      return false;
    }

    const opts = this.currentSocket.io && this.currentSocket.io.opts;
    if (!opts || this.currentSocketToken === newToken) {
      return false;
    }

    opts.query = { ...(opts.query || {}), token: newToken };
    this.currentSocketToken = newToken;

    if (this.currentSocket.connected) {
      return false;
    }

    if (this.reconnectWithTokenTimer) {
      clearTimeout(this.reconnectWithTokenTimer);
    }
    this.reconnectWithTokenTimer = setTimeout(() => {
      this.reconnectWithTokenTimer = null;
      if (this.currentSocket && !this.currentSocket.connected) {
        console.log("[Socket] Reconectando com o token renovado");
        this.socketReady = false;
        this.currentSocket.connect();
      }
    }, 1500);

    return true;
  },

  // Derruba o socket atual para que o próximo getSocket o recrie do zero.
  reset: function() {
    if (this.reconnectWithTokenTimer) {
      clearTimeout(this.reconnectWithTokenTimer);
      this.reconnectWithTokenTimer = null;
    }
    if (this.currentSocket) {
      this.currentSocket.removeAllListeners();
      this.currentSocket.disconnect();
      this.currentSocket = null;
    }
    this.socketReady = false;
    this.currentSocketToken = null;
    this.currentCompanyId = -1;
    this.currentUserId = -1;
  },

  getSocket: function(companyId) {
    let userId = null;
    if (localStorage.getItem("userId")) {
      userId = localStorage.getItem("userId");
    }

    // Sem companyId não há sala válida: seguir adiante criava assinaturas em
    // `company-null-*`, que nunca recebem nada.
    if (!companyId) {
      return new DummySocket();
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`;
    }

    if (companyId !== this.currentCompanyId || userId !== this.currentUserId) {
      if (this.currentSocket) {
        this.currentSocket.removeAllListeners();
        this.currentSocket.disconnect();
        this.currentSocket = null;
        this.socketReady = false;
      }

      let token = null;
      try {
        token = JSON.parse(localStorage.getItem("token"));
      } catch (parseError) {
        token = null;
      }

      if (!token) {
        return new DummySocket();
      }

      try {
        const { exp } = jwtDecode(token);
        if (Date.now() >= exp * 1000) {
          // Token vencido: pede renovação à camada de auth e devolve um socket
          // inerte enquanto isso. Nada é apagado do localStorage aqui.
          this.requestTokenRenewal();
          return new DummySocket();
        }
      } catch (decodeError) {
        // Token ilegível é problema da camada de auth: apagá-lo aqui só tornaria
        // o 401 seguinte invisível para o usuário.
        this.requestTokenRenewal();
        return new DummySocket();
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;
      this.currentSocketToken = token;

      // Em produção, usa polling primeiro para evitar erros de WebSocket no console
      // Depois faz upgrade para WebSocket quando a conexão estabilizar
      const now = Date.now();
      const shouldPausePolling = now < this.pollingPausedUntil;
      const transportConfig = process.env.NODE_ENV === 'production' 
        ? (shouldPausePolling ? ["websocket"] : ["polling", "websocket"])
        : ["websocket", "polling"]; // Dev: tenta WebSocket direto

      const reconnectAttempts = Number(process.env.REACT_APP_SOCKET_RECONNECT_ATTEMPTS || 120);
      const reconnectionDelay = Number(process.env.REACT_APP_SOCKET_RECONNECT_DELAY_MS || 1000);
      const reconnectionDelayMax = Number(process.env.REACT_APP_SOCKET_RECONNECT_DELAY_MAX_MS || 30000);
      const randomizationFactor = Number(process.env.REACT_APP_SOCKET_RANDOMIZATION_FACTOR || 0.5);
      
      this.currentSocket = openSocket(process.env.REACT_APP_BACKEND_URL, {
        transports: transportConfig,
        upgrade: true,                // Permite upgrade de polling para websocket
        pingTimeout: 60000,           // 60s - deve ser igual ou maior que o backend
        pingInterval: 25000,          // 25s - deve ser igual ou menor que o backend
        reconnection: true,           // Habilitar reconexão automática
        reconnectionAttempts: reconnectAttempts,
        reconnectionDelay,
        reconnectionDelayMax,
        randomizationFactor,
        timeout: 20000,               // Timeout de conexão inicial
        query: { token },
      });
      
      // Lidar com erros de conexão com sistema de grace period
      this.currentSocket.on("connect_error", (error) => {
        const now = Date.now();
        const timeSinceLastDisconnect = this.lastDisconnectTime ? now - this.lastDisconnectTime : Infinity;
        
        // Se desconectou recentemente (dentro do grace period), não loga
        const isQuickReconnectionAttempt = timeSinceLastDisconnect < this.gracePeriodMs;
        
        if (!isQuickReconnectionAttempt) {
          this.consecutiveErrors++;
          
          // Só loga após muitos erros E a cada 10 tentativas para evitar spam
          // (já que agora tenta infinitamente)
          if (this.consecutiveErrors >= 10 && this.consecutiveErrors % 10 === 0) {
            console.warn(`[Socket] Ainda tentando reconectar... (${this.consecutiveErrors} tentativas)`);
          }
        }
        // Silencia erros durante reconexões rápidas (quando servidor reinicia)
      });
      
      // Lidar com tentativas de reconexão (silencioso em produção)
      this.currentSocket.on("reconnect_attempt", () => {
        const currentTime = Date.now();

        if (!this.reconnectWindowStart || (currentTime - this.reconnectWindowStart) > this.reconnectWindowMs) {
          this.reconnectWindowStart = currentTime;
          this.reconnectAttemptsInWindow = 0;
        }

        this.reconnectAttemptsInWindow += 1;

        if (this.reconnectAttemptsInWindow >= this.maxReconnectsPerWindow) {
          this.reconnectCooldownUntil = currentTime + this.reconnectCooldownMs;
          this.pollingPausedUntil = this.reconnectCooldownUntil;

          if (this.currentSocket?.io?.opts) {
            this.currentSocket.io.opts.transports = ["websocket"];
          }

          this.currentSocket.disconnect();
          setTimeout(() => {
            if (this.currentSocket && !this.currentSocket.CONNECTED && Date.now() >= this.reconnectCooldownUntil) {
              this.reconnectWindowStart = Date.now();
              this.reconnectAttemptsInWindow = 0;
              this.currentSocket.connect();
            }
          }, this.reconnectCooldownMs);
        }
      });
      
      this.currentSocket.on("reconnect", (attemptNumber) => {
        const now = Date.now();
        const reconnectionDuration = this.lastDisconnectTime ? now - this.lastDisconnectTime : 0;
        
        // Resetar contadores de erro após reconexão bem-sucedida
        this.consecutiveErrors = 0;
        this.lastDisconnectTime = null;
        
        // Cancelar timeout de alerta se existir
        if (this.reconnectionTimeout) {
          clearTimeout(this.reconnectionTimeout);
          this.reconnectionTimeout = null;
        }
        
        // SEMPRE loga reconexões (conforme solicitado)
        console.log(`✅ [Socket] Reconectado com sucesso após ${Math.round(reconnectionDuration / 1000)}s (tentativa ${attemptNumber})`);
      });
      
      this.currentSocket.on("reconnect_failed", () => {
        console.error("[Socket] Reconnection failed after all attempts");
      });
      
      // Lidar com token inválido (sessão substituída por um login mais recente)
      this.currentSocket.on("tokenInvalid", () => {
        clearSessionKeys();
        // Deixa o recado para a tela de login explicar o que houve, em vez de
        // largar o usuário num formulário sem contexto.
        localStorage.setItem("sessionExpired", "true");
        window.location.href = "/login";
      });
      
      this.currentSocket.on("disconnect", (reason) => {
        this.socketReady = false;
        const now = Date.now();
        this.lastDisconnectTime = now;
        
        // Agendar alerta apenas se não reconectar em 60 segundos
        this.reconnectionTimeout = setTimeout(() => {
          if (!this.socketReady && this.lastDisconnectTime === now) {
            console.error(`[Socket] ⚠️ Failed to reconnect for 60s. Last reason: ${reason}`);
          }
        }, 60000);
        
        // IMPORTANTE: Verificar desconexão forçada pelo servidor PRIMEIRO
        if (reason === "io server disconnect") {
          // Relê o token do localStorage em vez de usar o `token` capturado na
          // criação do socket: se ele foi renovado nesse meio tempo, reconectar
          // com o valor antigo faria o servidor recusar de novo a cada 2s, para
          // sempre.
          let currentToken = null;
          try {
            currentToken = JSON.parse(localStorage.getItem("token"));
          } catch (e) {
            currentToken = null;
          }

          if (!currentToken) {
            return;
          }

          // Token renovado por outro caminho (X-New-Token ou renovação
          // periódica): reconecta já com a credencial nova.
          if (this.applyToken(currentToken)) {
            return;
          }

          try {
            const { exp } = jwtDecode(currentToken);
            if (Date.now() >= exp * 1000) {
              // Sem limpar a sessão: quem decide deslogar é a camada de auth.
              this.requestTokenRenewal();
              return;
            }
          } catch (e) {
            return;
          }

          // Se o token ainda é válido, tenta reconectar após um pequeno delay
          setTimeout(() => {
            if (
              this.currentSocket &&
              !this.currentSocket.CONNECTED &&
              Date.now() >= this.reconnectCooldownUntil
            ) {
              this.currentSocket.connect();
            }
          }, 2000);
          return;
        }
        
        // Para 'transport close' e 'ping timeout', socket.io reconecta automaticamente
        // Para 'io client disconnect', não reconecta
      });
      
      this.currentSocket.on("connect", () => {
        // Cancelar timeout de alerta se existir
        if (this.reconnectionTimeout) {
          clearTimeout(this.reconnectionTimeout);
          this.reconnectionTimeout = null;
        }
        
        // Resetar contadores
        this.consecutiveErrors = 0;
        
        // Log de conexão inicial bem-sucedida (apenas na primeira vez)
        if (!this.socketReady) {
          console.log(`✅ [Socket] Conectado com sucesso ao servidor`);
        }
      })
      
      // onAny removido para reduzir logs em produção
      
      // Listener PERSISTENTE: o servidor emite "ready" a cada (re)conexão
      // autenticada. Antes era `onReady` (= once), consumido na primeira
      // conexão; após a primeira queda socketReady ficava false para sempre.
      this.currentSocket.on("ready", () => {
        this.socketReady = true;
      });

    }
    
    return new ManagedSocket(this);
  },
  
  // Devolve o listener registrado (ou null se já disparou) para que o
  // ManagedSocket consiga removê-lo no disconnect().
  onReady: function( callbackReady ) {
    if (this.socketReady) {
      callbackReady();
      return null;
    }

    if (!this.currentSocket) {
      return null;
    }
    
    const listener = () => {
      callbackReady();
    };
    this.currentSocket.once("ready", listener);
    return listener;
  },

};

const SocketContext = createContext()

// Expor SocketManager globalmente para debug (apenas em desenvolvimento)
if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location.hostname === 'localhost')) {
  if (typeof window !== 'undefined') {
    window.SocketManager = SocketManager;
  }
}

export { SocketContext, SocketManager };
